import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";
import Button from "./Button";
import { FoodCategory } from "../../../types";
import { BiRestaurant } from "react-icons/bi";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { useStateValue } from "../../context/StateProvider";
import {
  CategoryObj,
  childrenOf,
  descendantTypes,
  isChildOf,
  resolveActiveRoot,
} from "../../utils/categories";

// How many category pills to show before collapsing the rest. Two rows on a
// phone, one on desktop — enough to signal "there are more" without pushing the
// products below the fold.
const COLLAPSED_COUNT = 11;

const ALL_CATEGORY = {
  id: 664,
  name: "Menu",
  urlParam: "all",
  icon: <BiRestaurant />,
  createdAt: Number.MIN_SAFE_INTEGER,
} as FoodCategory;

const Filters = ({
  filter,
  setFilter,
  restrictTo,
}: {
  filter: string;
  setFilter: any;
  /** Limits the bar to these categoryTypes — set when a section is open. */
  restrictTo?: Set<string> | null;
}) => {
  const [{ categories, foodItems }] = useStateValue();
  const [expanded, setExpanded] = useState(false);

  const raw = useMemo(() => categories || [], [categories]);

  // Categories are maintained independently of products, so the list contains
  // entries nothing is filed under. Showing them puts pills in the bar that
  // open an empty grid, so they are filtered out on what actually loaded.
  const stocked = useMemo(() => {
    const s = new Set<string>();
    (foodItems || []).forEach((p: any) => p?.category && s.add(p.category));
    return s;
  }, [foodItems]);

  const hasStock = useMemo(
    () => (type: string) =>
      Array.from(descendantTypes(raw, type)).some((t) => stocked.has(t)),
    [raw, stocked]
  );

  const sorted: FoodCategory[] = useMemo(
    () =>
      raw
        // Sub-categories are rolled up into their parent, so only roots get a
        // top-row pill. They reappear as a sub-row once the parent is picked.
        .filter((c: any) => !isChildOf(raw, c))
        .filter((c: any) => hasStock(c.categoryType))
        // Inside a section, only that section's categories are offered. A root
        // qualifies if any part of its subtree belongs to the section.
        .filter(
          (c: any) =>
            !restrictTo ||
            Array.from(descendantTypes(raw, c.categoryType)).some((t) =>
              restrictTo.has(t)
            )
        )
        .map((category: any) => CategoryObj(category))
        .sort(
          (a: { createdAt: number }, b: { createdAt: number }) =>
            a?.createdAt - b?.createdAt
        ),
    [raw, hasStock, restrictTo]
  );

  // Stays put while a child is selected, so picking one does not collapse the
  // list it was picked from.
  const activeRoot = useMemo(
    () => resolveActiveRoot(raw, filter),
    [raw, filter]
  );

  const subCategories: FoodCategory[] = useMemo(() => {
    if (!activeRoot) return [];
    return childrenOf(raw, activeRoot.id)
      .filter((c: any) => hasStock(c.categoryType))
      .map((c: any) => CategoryObj(c))
      .sort(
        (a: { createdAt: number }, b: { createdAt: number }) =>
          a?.createdAt - b?.createdAt
      );
  }, [raw, activeRoot, hasStock]);

  // When collapsed we still show the active root even if it sits past the
  // cut-off — otherwise selecting from the expanded list and collapsing again
  // hides the very filter that is currently applied.
  const visible = useMemo(() => {
    if (expanded) return sorted;
    const head = sorted.slice(0, COLLAPSED_COUNT);
    const active = sorted.find((c) => c.urlParam === activeRoot?.categoryType);
    return active && !head.includes(active) ? [...head, active] : head;
  }, [sorted, expanded, activeRoot]);

  const hiddenCount = sorted.length - Math.min(sorted.length, COLLAPSED_COUNT);

  return (
    <div className="w-full py-6 px-2">
      <motion.div
        layout
        className="w-full flex flex-wrap items-center justify-center gap-2 sm:gap-2.5"
      >
        <Button category={ALL_CATEGORY} filter={filter} setFilter={setFilter} />

        <AnimatePresence initial={false}>
          {visible.map((category: FoodCategory) => (
            <motion.div
              key={category.id}
              layout
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              transition={{ duration: 0.15 }}
            >
              <Button
                category={category}
                filter={
                  // Keep the parent looking selected while browsing a child.
                  activeRoot?.categoryType === category.urlParam
                    ? category.urlParam
                    : filter
                }
                setFilter={setFilter}
                hasChildren={childrenOf(raw, String(category.id)).some((c: any) =>
                  hasStock(c.categoryType)
                )}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {hiddenCount > 0 && (
        <div className="w-full flex items-center justify-center mt-4">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="flex items-center gap-1 text-xs sm:text-sm font-semibold
              text-textColor hover:text-cartNumBg transition-colors duration-150"
          >
            {expanded ? (
              <>
                Show less <MdExpandLess className="text-base" />
              </>
            ) : (
              <>
                Show {hiddenCount} more <MdExpandMore className="text-base" />
              </>
            )}
          </button>
        </div>
      )}

      {/* Sub-categories of the open parent. */}
      <AnimatePresence initial={false}>
        {subCategories.length > 0 && activeRoot && (
          <motion.div
            key={activeRoot.id}
            layout
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-5 mx-auto max-w-4xl rounded-2xl bg-containerbg border border-black/5 px-4 py-4">
              <p className="text-[11px] uppercase tracking-wide font-bold text-lightGray mb-3 text-center">
                {activeRoot.title} — browse by type
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {/* Back to the rolled-up view of everything under this parent. */}
                <Button
                  category={
                    {
                      id: `${activeRoot.id}-all`,
                      name: `All ${activeRoot.title}`,
                      urlParam: activeRoot.categoryType,
                      icon: <BiRestaurant />,
                      createdAt: Number.MIN_SAFE_INTEGER,
                    } as unknown as FoodCategory
                  }
                  filter={filter}
                  setFilter={setFilter}
                />
                {subCategories.map((category: FoodCategory) => (
                  <Button
                    key={category.id}
                    category={category}
                    filter={filter}
                    setFilter={setFilter}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Filters;
